
import { defineComponent, PropType, ref, reactive, onMounted, onBeforeUpdate, watch, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import { RepositoryFactory } from '@/repositories/RepositoryFactory'
const SeasonGameRepository = RepositoryFactory.get('seasonGame')

import draggable from 'vuedraggable'

import BaseDialog, { Dialog } from '@/components/BaseDialog.vue'

import CreateMatches from '@/components/seasonGame/CreateMatches.vue'
import RequestGrouping from '@/components/seasonGame/RequestGrouping.vue'

import { Game } from '@/types/game'
import { Custom } from '@/types/custom'
import { Group } from '@/types/group'
import { Field } from '@/types/field'
import { EntryTeam } from '@/types/entryTeam'


interface SortableGameForm {
  temporary_id?: number

  id?: number
  group_id: number
  priority: number
  match_no: string
  date: string
  field_id?: number | null
  home_team_id?: number
  away_team_id?: number
  official: string
  refree: string
}

interface Groups {
  customList: Custom[]
  custom?: Custom
  groupList: Group[]
  group?: Group
}

export default defineComponent({
  inheritAttrs: false,
  components: {
    draggable,

    BaseDialog,

    CreateMatches,
    RequestGrouping
  },
  props: {
    groups: {
      type: Object as PropType<Groups>
    },
    fields: {
      type: Array as PropType<Field[]>
    },
    isMobile: {
      type: Boolean
    }
  },
  setup(props) {
    const route = useRoute()
    const router = useRouter()
    const store = useStore()

    onMounted(() => {
      getGames()
    })

    watch(
      () => [route.query.custom, route.query.group],
      () => {
        if (route.name === 'seasonGame') {
          getGames()

          resetGameElementColor()
        }
      }
    )

    watch(
      () => props.groups,
      () => {
        setEntries()
      },
      { deep: true }
    )

    onBeforeUpdate(() => {
      scheduleTableRows.value = []
    })

    const entries: {
      list: EntryTeam[]
    } = reactive({
      list: []
    })

    const setEntries = () => {
      entries.list = props.groups!.groupList.map((group: Group) => group.teams).flat()
    }

    const games: {
      list: SortableGameForm[]
      scheduleAddNumber: number
      isLoading: boolean
    } = reactive({
      list: [],
      scheduleAddNumber: 5,
      isLoading: false
    })

    const existGameList = computed(() => {
      return games.list.length > 0
    })

    const existEntryTeams = computed(() => {
      if (props.groups?.group == undefined) return
      return props.groups?.group?.teams.length > 1
    })

    const matchType = computed(() => {
      return props.groups?.custom?.match_type_id === 1 ? 'league' : 'tournament'
    })

    const selectedGame = ref()
    const gamesCounter = ref(0)

    const getGames = async () => {
      try {
        games.isLoading = true

        const { season_id } = route.params
        const custom_id = route.query.custom
        const group_id = route.query.group

        const dataObj = {
          custome_id: custom_id,
          group_id: group_id
        }

        const res = await SeasonGameRepository.getGames(season_id, dataObj)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          gamesCounter.value = 0
          gameIdsToBeDeleted.value = []

          const gameList = data.games
          games.list = []

          gameList.forEach((game: Game) => {
            games.list.push({
              temporary_id: gamesCounter.value++,

              id: game.id,
              group_id: game.group_id,
              priority: game.priority,
              match_no: game.match_no,
              date: game.date,
              field_id: game.field_id,
              home_team_id: game.home?.id,
              away_team_id: game.away?.id,
              official: game.official,
              refree: game.refree
            })
          })
        }
      } catch (error) {
        //
      } finally {
        games.isLoading = false
      }
    }

    const addScheduleRow = ()=> {
      const grpup_id = route.query.group ? props.groups?.group?.id : props.groups?.custom?.groups[0].id
      for (let i=0; i < games.scheduleAddNumber; i++) {
        games.list.push({
          temporary_id: gamesCounter.value++,

          id: undefined,
          group_id: grpup_id!,
          priority: games.list.length + 1,
          match_no: '',
          date: '',
          field_id: null,
          home_team_id: undefined,
          away_team_id: undefined,
          official: '',
          refree: ''
        })
      }
    }

    const scheduleTableRows = ref([])

    const postGames = async () => {
      try {
        const { season_id } = route.params
        const custom_id = route.query.custom

        games.list.forEach((game, index) => {
          game.priority = index + 1
          delete game.temporary_id
        })

        const dataObj = {
          custome_id: custom_id,
          list: games.list
        }

        const res = await SeasonGameRepository.postGames(season_id, dataObj)

        if (res.status === 200) {
          store.dispatch('addFlashMessage', {
            type: 'success',
            message: '変更を保存しました'
          })

          getGames()
        }
      } catch (error) {
        //
      }
    }

    const changeGameElementColor = (event: Event) => {
      const element: HTMLElement = event.target as HTMLElement
      element.dataset.changed = 'true'
    }

    const resetGameElementColor = () => {
      scheduleTableRows.value.forEach((tr: HTMLElement) => {
        const changedElements = tr.querySelectorAll('[data-changed="true"]')
        for (let i=0; i < changedElements.length; i++) {
          (changedElements[i] as HTMLElement).dataset.changed = 'false'
        }
      })
    }

    const resetGameTeam = (index: number) => {
      games.list[index].home_team_id = undefined
      games.list[index].away_team_id = undefined
    }

    const moveFocusScheduleElement = (event: KeyboardEvent, index: number)=> {
      const target = (event.target as HTMLInputElement)
      const tagName = target.tagName

      const value = target.value
      const inputStartPosition = (tagName === 'INPUT') ? target.selectionStart as number : 0

      const tableRows = scheduleTableRows.value
      const tableRow = tableRows[index] as HTMLElement

      const elementList = tableRow.querySelectorAll('.js-form-element')
      const elementListArray: Array<HTMLElement> = [].slice.call(elementList)

      const upperTableRow = tableRows[index - 1] as HTMLElement
      const upperElementList = upperTableRow?.querySelectorAll('.js-form-element')

      const lowerTableRow = tableRows[index + 1] as HTMLElement
      const lowerElementList = lowerTableRow?.querySelectorAll('.js-form-element')

      const targetIndex: number = elementListArray.indexOf(target) as number

      switch (event.key) {
      case 'Up': // IE/Edge specific value
      case 'ArrowUp':
        if (tagName === 'SELECT') return
        if (upperElementList) (upperElementList[targetIndex] as HTMLElement).focus()
        break
      case 'Down': // IE/Edge specific value
      case 'ArrowDown':
        if (tagName === 'SELECT') return
        if (lowerElementList) (lowerElementList[targetIndex] as HTMLElement).focus()
        break
      case 'Left': // IE/Edge specific value
      case 'ArrowLeft':
        if (tagName === 'INPUT' && inputStartPosition !== 0) return
        if (targetIndex === 0) {
          if (upperElementList) (upperElementList[upperElementList.length - 1] as HTMLElement).focus()
        } else {
          (elementList[targetIndex - 1] as HTMLElement).focus()
        }
        break
      case 'Right': // IE/Edge specific value
      case 'ArrowRight':
        if (tagName === 'INPUT' && inputStartPosition < value.length) return
        if (targetIndex + 1 === elementListArray.length) {
          if (lowerElementList) (lowerElementList[0] as HTMLElement).focus()
        } else {
          (elementList[targetIndex + 1] as HTMLElement).focus()
        }
        break
      default:
        return
      }
    }

    const gameIdsToBeDeleted = ref<number[]>([])

    const judgeDeleteGame = (game: SortableGameForm, index: number) => {
      const id = game.id
      if (id != undefined) {
        // 登録済みなので、オーバーレイかける
        gameIdsToBeDeleted.value.push(id)
      } else {
        //未登録なので、リストから削除のみ
        games.list.splice(index, 1)
      }
    }

    const deleteGame = async () => {
      try {
        const { season_id } = route.params
        const ids = gameIdsToBeDeleted.value

        const dataObj = {
          ids: ids
        }

        const res = await SeasonGameRepository.deleteGames(season_id, dataObj)

        if (res.status === 200) {
          games.list = games.list.filter(game => game.id == undefined || !gameIdsToBeDeleted.value.includes(game.id))

          postGames()
        }
      } catch (error) {
        //
      }
    }

    const restoreDeletedGame = (game: SortableGameForm) => {
      gameIdsToBeDeleted.value = gameIdsToBeDeleted.value.filter((id) => id !== game.id)
    }

    const onClickSaveGames = () => {
      switch (matchType.value) {
      case 'league':
        saveGames()
        break
      case 'tournament':
        openGamesDialogConfirm()
        break
      }
    }

    const saveGames = () => {
      if (gameIdsToBeDeleted.value.length > 0) {
        deleteGame()
      } else {
        postGames()
      }

      closeGamesDialogConfirm()
    }

    const gamesDialogConfirm = reactive<Dialog>({
      title: '変更を保存しますか？',
      message: '「試合の追加や削除」「対戦チームの入れ替え」を行った場合、トーナメント表が正しく表示されません。\nトーナメント表を利用しない場合のみ「変更を保存」を押してください。',
      confirmText: '変更を保存する',
      cancelText: 'キャンセル',

      isOpen: false
    })

    const openGamesDialogConfirm = () => {
      gamesDialogConfirm.isOpen = true
    }

    const closeGamesDialogConfirm = () => {
      gamesDialogConfirm.isOpen = false
    }


    const onClickCreateButton = () => {
      switch (matchType.value) {
      case 'league':
        createLeagueTable()
        break
      case 'tournament':
        toTournamentPage()
        break
      }
    }

    const createLeagueTable = async () => {
      try {
        const { season_id } = route.params
        const custom_id = route.query.custom
        const group_id = route.query.group

        const dataObj = {
          custome_id: custom_id,
          group_id: group_id
        }

        const res = await SeasonGameRepository.postGameLeague(season_id, dataObj)

        if (res.status === 200) {
          getGames()

          store.dispatch('addFlashMessage', {
            type: 'success',
            message: 'リーグ表を作成しました'
          })
        }
      } catch (error) {
        //
      }
    }

    const toTournamentPage = () => {
      const { custom, group } = route.query
      router.push({ query: { custom: custom, group: group, mode: 'tournament' }})
    }

    return {
      entries,

      getGames,
      gamesCounter,

      games,
      existGameList,
      existEntryTeams,
      matchType,
      addScheduleRow,
      scheduleTableRows,

      changeGameElementColor,
      resetGameElementColor,
      resetGameTeam,
      moveFocusScheduleElement,

      postGames,

      selectedGame,
      gameIdsToBeDeleted,

      judgeDeleteGame,
      deleteGame,
      restoreDeletedGame,

      onClickSaveGames,
      saveGames,

      gamesDialogConfirm,
      openGamesDialogConfirm,
      closeGamesDialogConfirm,

      onClickCreateButton,
      createLeagueTable,
      toTournamentPage
    }
  }
})
